// src/lib/sat/highlight-dom.ts
//
// Browser-DOM glue for passage highlights (#778): turn a user text selection
// into character offsets over a container, and re-apply stored offsets as
// <mark> elements over the rendered (dangerouslySetInnerHTML) stem.
//
// Offsets are measured over the container's text via Range.toString(), so they
// stay consistent whether we're reading a selection or re-wrapping it, and they
// work across element boundaries (formatting tags, table cells). Charts/diagrams
// render as <svg>; their text is counted for offset accounting but never wrapped
// (wrapping foreign content would corrupt the SVG).
import type { HighlightRange } from '@/lib/sat/highlights'
import { FIGURE_TEXT_SELECTOR } from '@/lib/render/figure-text'

/**
 * Outermost visually hidden figure-text subtrees under `root` (a graph's
 * description, a chart's data table). They are not visible, so they can never
 * be selected, and they are left out of the offset basis entirely — otherwise
 * adding a description would shift every saved highlight placed after a figure.
 */
function figureTextRoots(root: HTMLElement): Element[] {
  return Array.from(root.querySelectorAll(FIGURE_TEXT_SELECTOR)).filter((el) => {
    const outer = el.parentElement?.closest(FIGURE_TEXT_SELECTOR)
    return !outer || !root.contains(outer)
  })
}

function inFigureText(node: Node, root: HTMLElement): boolean {
  const el = node.nodeType === 1 ? (node as Element) : node.parentElement
  const hit = el?.closest(FIGURE_TEXT_SELECTOR)
  return !!hit && root.contains(hit)
}

/**
 * Length of `root`'s text in the highlight offset basis: its textContent minus
 * any figure-text subtrees. Use this, not textContent.length, to clamp offsets.
 */
export function highlightTextLength(root: HTMLElement): number {
  let len = root.textContent?.length ?? 0
  for (const el of figureTextRoots(root)) len -= el.textContent?.length ?? 0
  return len
}

/** Char offset from the start of `root`'s text to the (node, offset) boundary. */
function offsetOf(root: HTMLElement, node: Node, nodeOffset: number): number {
  const r = root.ownerDocument.createRange()
  r.selectNodeContents(root)
  try {
    r.setEnd(node, nodeOffset)
  } catch {
    return 0
  }
  let len = r.toString().length
  // Subtract the figure text that falls before the boundary.
  for (const el of figureTextRoots(root)) {
    const s = root.ownerDocument.createRange()
    s.selectNodeContents(el)
    const where = s.comparePoint(node, nodeOffset) // -1 before, 0 inside, 1 after
    if (where > 0) len -= s.toString().length
    else if (where === 0) {
      s.setEnd(node, nodeOffset)
      len -= s.toString().length
    }
  }
  return len
}

/** Convert a selection Range to {start,end} char offsets over `root`, or null. */
export function textOffsetsFromRange(
  root: HTMLElement,
  range: Range,
): { start: number; end: number } | null {
  if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return null
  const a = offsetOf(root, range.startContainer, range.startOffset)
  const b = offsetOf(root, range.endContainer, range.endOffset)
  const start = Math.min(a, b)
  const end = Math.max(a, b)
  return start < end ? { start, end } : null
}

/**
 * Inverse of textOffsetsFromRange: build a DOM Range spanning [start,end] char
 * offsets over `root`'s text (same basis). Used by the read-aloud tool (#791) to
 * highlight the sentence being spoken. Returns null if the offsets can't be
 * resolved. Clamps defensively rather than throwing.
 */
export function rangeFromOffsets(root: HTMLElement, start: number, end: number): Range | null {
  if (end <= start) return null
  const doc = root.ownerDocument
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let pos = 0
  let startNode: Text | null = null
  let startOffset = 0
  let endNode: Text | null = null
  let endOffset = 0
  let node = walker.nextNode() as Text | null
  while (node) {
    const len = node.data.length
    if (startNode === null && start < pos + len) {
      startNode = node
      startOffset = Math.max(0, start - pos)
    }
    if (startNode !== null && end <= pos + len) {
      endNode = node
      endOffset = Math.max(0, end - pos)
      break
    }
    pos += len
    node = walker.nextNode() as Text | null
  }
  if (!startNode || !endNode) return null
  const r = doc.createRange()
  try {
    r.setStart(startNode, Math.min(startOffset, startNode.data.length))
    r.setEnd(endNode, Math.min(endOffset, endNode.data.length))
  } catch {
    return null
  }
  return r
}

/** The current non-collapsed selection Range if it lives inside `root`, else null. */
export function selectionRangeWithin(root: HTMLElement): Range | null {
  const sel = root.ownerDocument.getSelection?.() ?? null
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null
  const range = sel.getRangeAt(0)
  if (!root.contains(range.commonAncestorContainer)) return null
  return range
}

function isInSvg(node: Node): boolean {
  let el = node.parentElement
  while (el) {
    if (el.tagName.toLowerCase() === 'svg') return true
    el = el.parentElement
  }
  return false
}

/** Unwrap all highlight <mark>s and re-merge text nodes, restoring clean text. */
export function clearHighlightMarks(root: HTMLElement): void {
  root.querySelectorAll('mark[data-hl]').forEach((m) => {
    const parent = m.parentNode
    if (!parent) return
    while (m.firstChild) parent.insertBefore(m.firstChild, m)
    parent.removeChild(m)
  })
  root.normalize()
}

function wrapOffsets(root: HTMLElement, start: number, end: number, id: string, hasNote: boolean) {
  const doc = root.ownerDocument
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const targets: { node: Text; from: number; to: number }[] = []
  let pos = 0
  let node = walker.nextNode() as Text | null
  while (node) {
    if (inFigureText(node, root)) {
      // Not part of the offset basis (see figureTextRoots).
      node = walker.nextNode() as Text | null
      continue
    }
    const len = node.data.length
    const from = Math.max(start, pos)
    const to = Math.min(end, pos + len)
    if (from < to && !isInSvg(node)) targets.push({ node, from: from - pos, to: to - pos })
    pos += len
    if (pos >= end) break
    node = walker.nextNode() as Text | null
  }
  // Each target is a fragment of a distinct text node, so order is irrelevant.
  for (const t of targets) {
    const r = doc.createRange()
    r.setStart(t.node, t.from)
    r.setEnd(t.node, t.to)
    const mark = doc.createElement('mark')
    mark.setAttribute('data-hl', id)
    mark.className = 'sat-hl'
    mark.setAttribute('role', 'button')
    mark.setAttribute('tabindex', '0')
    mark.setAttribute('aria-label', hasNote ? 'Highlight with a note' : 'Highlight')
    if (hasNote) mark.setAttribute('data-note', 'true')
    try {
      r.surroundContents(mark)
    } catch {
      // Single-text-node range — surroundContents is safe; ignore the rare throw.
    }
  }
}

/** Re-apply the given highlight ranges as <mark>s over `root` (idempotent). */
export function applyHighlights(root: HTMLElement, ranges: HighlightRange[]): void {
  clearHighlightMarks(root)
  for (const r of ranges) wrapOffsets(root, r.start, r.end, r.id, !!r.note)
}
